import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import Countdown from "@/components/site/Countdown";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowRight, Package, Zap, MapPin, Crown, Lock } from "lucide-react";
import { toast } from "sonner";
import { openCheckoutWithPromise } from "@/lib/checkout";

export default function ShopPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loadingId, setLoadingId] = useState(null);

  const load = () => api.get("/products").then((r) => setProducts(r.data || []));

  useEffect(() => {
    load();
  }, [user]);

  const buy = async (p) => {
    if (p.vip_only) {
      navigate("/tour-pass");
      return;
    }
    setLoadingId(p.id);
    try {
      await openCheckoutWithPromise(async () => {
        const { data } = await api.post("/checkout/session", {
          product_id: p.id,
          origin_url: window.location.origin,
        });
        return data.url;
      });
    } catch (e) {
      const msg = e?.response?.data?.detail || "Checkout failed. Try again.";
      toast(msg);
    } finally {
      setLoadingId(null);
    }
  };

  const sorted = [...products].sort((a, b) => {
    const ax = a.is_limited && a.is_active ? 0 : 1;
    const bx = b.is_limited && b.is_active ? 0 : 1;
    if (ax !== bx) return ax - bx;
    return (b.drop_at || "").localeCompare(a.drop_at || "");
  });
  const activeDrop = sorted.find((p) => p.is_limited && p.is_active);

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-8 py-12" data-testid="shop-page">
      <div className="overline">Limited drops</div>
      <h1 className="font-display text-5xl md:text-6xl uppercase mt-3 drip-shadow">
        Mile marker shop
      </h1>
      <p className="text-white/70 mt-4 max-w-2xl">
        Every city I roll into spawns a 48-hour drop. Tour Pass holders get the first 12 hours all
        to themselves.
      </p>

      {activeDrop && (
        <div
          className={`mt-8 border p-5 md:p-6 flex flex-wrap gap-5 items-center justify-between ${
            activeDrop.vip_only
              ? "border-[#FFC107]/50 bg-[#141414]"
              : "border-[#FF1493]/50 bg-[#141414]"
          }`}
          data-testid="active-drop-banner"
        >
          <div className="flex items-center gap-4">
            <div
              className="w-16 h-16 bg-cover bg-center border border-white/10"
              style={{ backgroundImage: `url(${activeDrop.image_url})` }}
            />
            <div>
              <div className="overline flex items-center gap-2">
                {activeDrop.vip_only ? (
                  <>
                    <Crown size={12} className="text-[#FFC107]" /> VIP early access
                  </>
                ) : (
                  <>
                    <Zap size={12} /> Live drop
                  </>
                )}{" "}
                ·{" "}
                <span className="text-white/80 normal-case tracking-normal flex items-center gap-1">
                  <MapPin size={12} /> {activeDrop.city}
                </span>
              </div>
              <div className="font-display text-2xl uppercase mt-1">{activeDrop.name}</div>
            </div>
          </div>
          <div className="flex items-center gap-5">
            <div>
              <div className="overline">
                {activeDrop.vip_only ? "Public unlocks in" : "Ends in"}
              </div>
              <Countdown
                initialSeconds={
                  activeDrop.vip_only
                    ? activeDrop.seconds_until_public
                    : activeDrop.seconds_remaining
                }
                onExpire={load}
                className={`text-2xl ${activeDrop.vip_only ? "text-[#FFC107]" : "text-[#FF1493]"}`}
              />
            </div>
            {activeDrop.vip_only ? (
              <Link to="/tour-pass" className="btn-blaze" data-testid="active-drop-vip-cta">
                <Crown size={14} /> Get Tour Pass
              </Link>
            ) : (
              <button
                onClick={() => buy(activeDrop)}
                disabled={loadingId === activeDrop.id}
                className="btn-blaze"
                data-testid={`buy-active-${activeDrop.id}`}
              >
                <Package size={14} /> Cop it
              </button>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-5 mt-12">
        {sorted.map((p, idx) => {
          const isHero = idx === 0;
          const isLimited = !!p.is_limited;
          const expired = p.is_limited && !p.is_active;
          const vipOnly = !!p.vip_only;
          return (
            <article
              key={p.id}
              className={`tac-card overflow-hidden flex flex-col relative ${
                isHero ? "lg:col-span-8 lg:row-span-2" : "lg:col-span-4"
              } ${expired ? "opacity-60" : ""}`}
              data-testid={`product-${p.id}`}
            >
              <div className="absolute top-3 left-3 z-10 flex items-center gap-2">
                {isLimited && !vipOnly && (
                  <span className="flex items-center gap-1 bg-[#FF1493] text-white px-2 py-1 text-[10px] font-bold uppercase tracking-[0.2em]">
                    <Zap size={10} /> 48h
                  </span>
                )}
                {vipOnly && (
                  <span className="flex items-center gap-1 bg-[#FFC107] text-[#0A0A0A] px-2 py-1 text-[10px] font-bold uppercase tracking-[0.2em]">
                    <Crown size={10} /> VIP
                  </span>
                )}
              </div>
              <div
                className={`bg-cover bg-center ${isHero ? "aspect-[16/10]" : "aspect-[4/3]"} ${
                  vipOnly ? "grayscale" : ""
                }`}
                style={{ backgroundImage: `url(${p.image_url})` }}
              />
              <div className="p-6 flex-1 flex flex-col">
                <div className="flex items-center justify-between">
                  <span className="overline">
                    {p.city ? `Mile · ${p.city}` : `Mile ${String(idx + 1).padStart(3, "0")}`}
                  </span>
                  <span className="font-mono-tactical text-xs text-white/60">
                    {p.stock} units
                  </span>
                </div>
                <h3 className={`font-display uppercase mt-3 ${isHero ? "text-5xl" : "text-2xl"}`}>
                  {p.name}
                </h3>
                <p className="text-white/70 text-sm mt-3 line-clamp-3">{p.description}</p>
                {isLimited && !vipOnly && (
                  <div className="mt-4 flex items-center gap-2 text-xs">
                    <span className="overline">Ends in</span>
                    <Countdown
                      initialSeconds={p.seconds_remaining}
                      onExpire={load}
                      className={expired ? "text-white/40" : "text-[#FF1493]"}
                    />
                  </div>
                )}
                {vipOnly && (
                  <div className="mt-4 flex items-center gap-2 text-xs">
                    <span className="overline">Public in</span>
                    <Countdown
                      initialSeconds={p.seconds_until_public}
                      onExpire={load}
                      className="text-[#FFC107]"
                    />
                  </div>
                )}
                <div className="mt-6 flex items-center justify-between">
                  <span className="font-display text-3xl">${p.price.toFixed(2)}</span>
                  {vipOnly ? (
                    <Link
                      to="/tour-pass"
                      className="btn-blaze"
                      data-testid={`vip-${p.id}`}
                    >
                      <Lock size={14} /> Unlock
                    </Link>
                  ) : (
                    <button
                      onClick={() => buy(p)}
                      disabled={loadingId === p.id || expired}
                      className="btn-blaze"
                      data-testid={`buy-${p.id}`}
                    >
                      <Package size={14} />
                      {expired ? "Closed" : loadingId === p.id ? "Loading..." : "Cop it"}
                      {!expired && <ArrowRight size={14} />}
                    </button>
                  )}
                </div>
              </div>
            </article>
          );
        })}
      </div>
      {sorted.length === 0 && (
        <div className="text-white/60 text-center py-20">No drops live right now. New batch incoming.</div>
      )}
    </div>
  );
}
